<template>
  <!-- Transition Overlay for Module Loading -->
  <transition-overlay
    :is-visible="showTransitionOverlay"
    :module-title="transitionData.title"
    :module-icon="transitionData.icon"
    :module-color="transitionData.color"
    :loading-message="transitionData.message"
    :is-dark="isDarkMode"
    @loading-complete="handleTransitionComplete"
  />

  <!-- Navigation with Fade Transition -->
  <transition name="navigation-fade" mode="out-in">
    <sidenav
      v-if="this.$store.state.showSidenav && !shouldHideNavigation && !isNavigationHiding"
      :custom_class="this.$store.state.mcolor"
      :class="[
        this.$store.state.isTransparent,
        this.$store.state.isRTL ? 'fixed-end' : 'fixed-start',
        { 'navigation-fading': isNavigationHiding }
      ]"
    />
  </transition>

  <main
    class="main-content position-relative max-height-vh-100 h-100 border-radius-lg"
    :style="mainContentStyle"
    :class="{
      'full-screen': shouldHideNavigation,
      'content-transitioning': isNavigationHiding
    }"
    id="main-content"
    role="main"
  >
    <!-- nav with transition -->
    <transition name="navigation-fade" mode="out-in">
      <navbar
        v-if="this.$store.state.showNavbar && !shouldHideNavigation && !isNavigationHiding"
        :class="[navClasses, { 'navigation-fading': isNavigationHiding }]"
        :textWhite="this.$store.state.isAbsolute ? 'text-white opacity-8' : ''"
        :minNav="isNavbarMinimized"
      />
    </transition>

    <!-- Router view with page transitions -->
    <router-view v-slot="{ Component, route }">
      <transition
        :name="getTransitionName(route)"
        mode="out-in"
        @before-enter="onPageTransitionStart"
        @after-enter="onPageTransitionEnd"
      >
        <div :key="route.path">
          <component :is="Component" />
        </div>
      </transition>
    </router-view>

    <app-footer v-show="this.$store.state.showFooter" id="footer-main" />
    <bottom-nav />
  </main>
  
</template>

<script>
import { mapMutations, mapState } from "vuex";
import Sidenav from "./examples/Sidenav";
import Navbar from "@/examples/Navbars/Navbar.vue";
import AppFooter from "@/examples/Footer.vue";
import BottomNav from "@/examples/BottomNav.vue";
import TransitionOverlay from "@/components/TransitionOverlay.vue";
import { eventBus } from "@/utils/eventBus.js";

export default {
  name: "App",
  components: {
    Sidenav,
    Navbar,
    AppFooter,
    BottomNav,
    TransitionOverlay,
  },
  
  
  data() {
    return {
      previousRoute: null,
      currentRoute: null,
      isPageTransitioning: false,
      isNavigationHiding: false,
      showTransitionOverlay: false,
      transitionData: {
        title: '',
        icon: '',
        color: '',
        message: ''
      },
      navigationHideTimeout: null
    }
  },
  
  computed: {
    ...mapState({
      isNavbarMinimized: 'isNavbarMinimized'
    }),

    shouldHideNavigation() {
      return this.$route?.meta?.hideNavigation || false;
    },

    mainContentStyle() {
      const baseStyle = this.$store.state.isRTL ? 'overflow-x: hidden' : '';
      return baseStyle;
    },

    navClasses() {
      return {
        "position-sticky blur shadow-blur mt-4 left-auto top-1 z-index-sticky": this
          .$store.state.isNavFixed,
        "position-absolute px-4 mx-0 w-100 z-index-2": this.$store.state
          .isAbsolute,
        "px-0 mx-2 mt-3": !this.$store.state.isAbsolute,
      };
    },

    isDarkMode() {
      return document.body.classList.contains('dark-mode-active');
    }
  },

  watch: {
    '$route'(to, from) {
      this.previousRoute = from
      this.currentRoute = to
      this.handleRouteChange(to, from)
    }
  },
  
  beforeMount() {
    this.$store.state.isTransparent = "bg-transparent";
  },
  
  mounted() {
    // Initialize route tracking
    this.currentRoute = this.$route

    // Add skip link for accessibility
    // this.addSkipLink()

    // Listen for 4Task transition event from SidenavCollapse
    eventBus.on('show-4task-transition', this.show4TaskTransition)
  },

  beforeUnmount() {
    // Clean up event listener
    eventBus.off('show-4task-transition', this.show4TaskTransition)
  },
  
  methods: {
    ...mapMutations(["navbarMinimize"]),

    handleRouteChange(to, from) {
      // Check if we need to show the transition overlay
      if (to.meta?.preloader && to.meta?.moduleInfo) {
        this.startModuleTransition(to);
      } else if (from?.meta?.hideNavigation && !to.meta?.hideNavigation) {
        // Transitioning back from a hidden nav module
        this.startNavigationShow();
      }
    },

    startModuleTransition(route) {
      const moduleInfo = route.meta.moduleInfo;
      this.transitionData = {
        title: moduleInfo.title || 'Cargando módulo',
        icon: moduleInfo.icon || 'ri-loader-line',
        color: moduleInfo.color || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        message: route.meta.loadingMessage || 'Preparando interfaz...'
      };

      // Start navigation hide transition
      if (route.meta.hideNavigation) {
        this.isNavigationHiding = true;
      }

      this.showTransitionOverlay = true;
    },

    handleTransitionComplete() {
      this.showTransitionOverlay = false;
      this.isNavigationHiding = false;
    },

    startNavigationShow() {
      this.isNavigationHiding = false;
    },

    getTransitionName(route) {
      if (route.meta?.hideNavigation) {
        return 'module-slide';
      }
      return 'page-fade';
    },

    onPageTransitionStart() {
      this.isPageTransitioning = true;
    },

    onPageTransitionEnd() {
      this.isPageTransitioning = false;
    },

    addSkipLink() {
      const skipLink = document.createElement('a')
      skipLink.href = '#main-content'
      skipLink.textContent = 'Saltar al contenido principal'
      skipLink.className = 'skip-link'
      skipLink.style.cssText = `
        position: absolute;
        top: -40px;
        left: 6px;
        background: var(--primary-600, #2563eb);
        color: white;
        padding: 8px;
        text-decoration: none;
        border-radius: 4px;
        z-index: 1000;
        transition: top 0.3s ease;
      `

      skipLink.addEventListener('focus', () => {
        skipLink.style.top = '6px'
      })

      skipLink.addEventListener('blur', () => {
        skipLink.style.top = '-40px'
      })

      document.body.insertBefore(skipLink, document.body.firstChild)
    },

    show4TaskTransition() {

      // Configurar datos de transición para 4Task
      this.transitionData = {
        title: '4Task',
        icon: 'ri-task-line',
        color: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        message: 'Abriendo workspace en nueva pestaña...'
      }

      // Mostrar overlay
      this.showTransitionOverlay = true

      // Configurar timer para completar transición y abrir en nueva pestaña
      setTimeout(() => {
        this.handle4TaskTransitionComplete()
      }, 1500)
    },

    handle4TaskTransitionComplete() {
      // Ocultar overlay
      this.showTransitionOverlay = false

      // Abrir 4Task en nueva pestaña
      this.open4TaskInNewTab()
    },

    open4TaskInNewTab() {
      // Construir la URL completa para 4Task
      const route = this.$router.resolve({ name: '4Task' })
      const baseUrl = window.location.origin
      const fullUrl = baseUrl + route.href

      // Abrir en nueva pestaña
      const newTab = window.open(fullUrl, '_blank')

      // Enfocar la nueva pestaña si se abrió correctamente
      if (newTab) {
        newTab.focus()
        console.log('4Task abierto en nueva pestaña')
      } else {
        // Fallback si el navegador bloqueó el popup
        console.warn('No se pudo abrir 4Task en nueva pestaña, navegando en la misma pestaña')
        this.$router.push({ name: '4Task' })
      }
    }
  }
};
</script>

<style>
/* Asegurar espacio para el bottom navigation en móviles */
@media (max-width: 991.98px) {
  .main-content {
    padding-bottom: calc(70px + env(safe-area-inset-bottom)) !important;
  }
  
  /* Ajustar el footer para que no interfiera con el bottom nav */
  .footer {
    padding-bottom: calc(70px + env(safe-area-inset-bottom));
  }
}

/* Evitar scroll horizontal en dispositivos móviles */
@media (max-width: 991.98px) {
  body {
    overflow-x: hidden;
  }
  
  .main-content {
    overflow-x: hidden;
  }
}

/* Screen reader only text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Full screen mode for views without navigation */
.main-content.full-screen {
  width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding: 0 !important;
  border-radius: 0 !important;
  position: relative !important;
}

/* Transition styles for smooth navigation */
.main-content.content-transitioning {
  transition: all 0.5s ease-in-out;
  transform: translateX(0);
}

.navigation-fading {
  opacity: 0.5;
  transform: translateX(-10px);
  transition: all 0.3s ease-out;
}

/* Navigation fade transitions */
.navigation-fade-enter-active,
.navigation-fade-leave-active {
  transition: all 0.3s ease;
}

.navigation-fade-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.navigation-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Page transitions */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: all 0.3s ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Module slide transition for special modules like 4Task */
.module-slide-enter-active {
  transition: all 0.5s ease-out;
}

.module-slide-leave-active {
  transition: all 0.3s ease-in;
}

.module-slide-enter-from {
  opacity: 0;
  transform: translateX(50px) scale(0.95);
}

.module-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px) scale(1.02);
}

/* Enhanced transitions for better UX */
@media (prefers-reduced-motion: no-preference) {
  .main-content,
  .sidenav,
  .navbar {
    will-change: transform, opacity;
  }
}

/* Respect user's motion preferences */
@media (prefers-reduced-motion: reduce) {
  .navigation-fade-enter-active,
  .navigation-fade-leave-active,
  .page-fade-enter-active,
  .page-fade-leave-active,
  .module-slide-enter-active,
  .module-slide-leave-active,
  .navigation-fading,
  .content-transitioning {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
}

.el-dialog__title{
  color:white !important
}
.swal2-container {
  z-index: 9999 !important;
}

.rows {
    --bs-gutter-x: -0.5rem !important;
}
</style>